import { all, call, put, takeLatest } from "redux-saga/effects";
import { message } from "antd";
import {
  CREATE_DISCOUNT_REQUEST,
  DELETE_DISCOUNT_REQUEST,
  FETCH_DISCOUNT_REQUEST,
  FETCH_DISCOUNTS_REQUEST,
  FETCH_DISCOUNT_COUPONS_REQUEST,
  FETCH_DISCOUNT_APPROVALS_REQUEST,
  FETCH_DISCOUNT_HISTORY_REQUEST,
  GENERATE_DISCOUNT_COUPONS_REQUEST,
  MARK_DISCOUNT_COUPONS_PRINTED_REQUEST,
  MARK_DISCOUNT_COUPONS_USED_REQUEST,
  APPROVE_DISCOUNT_REQUEST,
  REJECT_DISCOUNT_REQUEST,
  UPDATE_DISCOUNT_REQUEST,
} from "constants/Discount";
import {
  createDiscountFailure,
  createDiscountSuccess,
  deleteDiscountFailure,
  deleteDiscountSuccess,
  fetchDiscountFailure,
  fetchDiscountRequest,
  fetchDiscountSuccess,
  fetchDiscountsFailure,
  fetchDiscountsSuccess,
  fetchDiscountCouponsFailure,
  fetchDiscountCouponsRequest,
  fetchDiscountCouponsSuccess,
  generateDiscountCouponsFailure,
  generateDiscountCouponsSuccess,
  markDiscountCouponsPrintedFailure,
  markDiscountCouponsPrintedSuccess,
  markDiscountCouponsUsedFailure,
  markDiscountCouponsUsedSuccess,
  updateDiscountFailure,
  updateDiscountSuccess,
  fetchDiscountApprovalsSuccess,
  fetchDiscountApprovalsFailure,
  approveDiscountSuccess,
  approveDiscountFailure,
  rejectDiscountSuccess,
  rejectDiscountFailure,
  fetchDiscountHistorySuccess,
  fetchDiscountHistoryFailure,
  fetchDiscountApprovalsRequest,
  fetchDiscountHistoryRequest,
} from "redux/actions";
import DiscountService from "services/DiscountService";
import CouponService from "services/Coupon";

function getPayloadData(result) {
  if (result === null || typeof result === "undefined") {
    const error = new Error("Empty server response");
    error.payload = result;
    throw error;
  }

  let payload = result;

  if (
    typeof result === "object" &&
    result !== null &&
    Object.prototype.hasOwnProperty.call(result, "status")
  ) {
    if (result.status !== true) {
      const error = new Error(result.message || "Request failed");
      error.payload = result;
      throw error;
    }
    payload = result.data ?? [];
  }

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;

  if (typeof payload !== "object") {
    const error = new Error("Unexpected response format from server");
    error.payload = payload;
    throw error;
  }

  return payload.data ?? payload;
}

export function* handleFetchDiscounts({ payload }) {
  try {
    const response = yield call(DiscountService.list, payload);
    const data = getPayloadData(response);
    yield put(fetchDiscountsSuccess(data));
  } catch (error) {
    yield put(fetchDiscountsFailure(error?.message || "Unable to load discounts"));
    message.error("Unable to load discounts");
  }
}

export function* handleFetchDiscountCoupons({ payload }) {
  try {
    const response = yield call(CouponService.index, {
      discount_id: payload.id,
      ...(payload.params || {}),
    });
    const data = getPayloadData(response);
    const meta = response?.data?.meta ?? response?.meta ?? null;
    yield put(fetchDiscountCouponsSuccess(payload.id, data, meta));
  } catch (error) {
    const messageText = error?.message || "Unable to load coupons";
    yield put(fetchDiscountCouponsFailure(messageText));
    message.error(messageText);
  }
}

export function* handleGenerateDiscountCoupons({ payload }) {
  try {
    const response = yield call(DiscountService.generateCoupons, payload.id, payload.data || {});
    const data = getPayloadData(response);
    yield put(generateDiscountCouponsSuccess(payload.id, data));
    const createdCount =
      data?.created_count ??
      (Array.isArray(data?.coupons?.data)
        ? data.coupons.data.length
        : Array.isArray(data?.coupons)
        ? data.coupons.length
        : 0);
    if (createdCount > 0) {
      message.success(`${createdCount} coupon${createdCount > 1 ? "s" : ""} generated`);
    } else {
      message.success("Coupons generated");
    }
    yield put(fetchDiscountCouponsRequest(payload.id));
    yield put(fetchDiscountRequest(payload.id));
  } catch (error) {
    const messageText = error?.message || "Unable to generate coupons";
    yield put(generateDiscountCouponsFailure(messageText));
    message.error(messageText);
  }
}

export function* handleMarkDiscountCouponsPrinted({ payload }) {
  try {
    if (!Array.isArray(payload.couponIds) || payload.couponIds.length === 0) {
      throw new Error("Select at least one coupon to mark as printed");
    }
    yield call(CouponService.print, payload.couponIds);
    yield put(markDiscountCouponsPrintedSuccess(payload.id));
    message.success("Coupon(s) marked as printed");
    yield put(fetchDiscountCouponsRequest(payload.id));
    yield put(fetchDiscountRequest(payload.id));
  } catch (error) {
    const messageText = error?.message || "Unable to mark coupons as printed";
    yield put(markDiscountCouponsPrintedFailure(messageText));
    message.error(messageText);
  }
}

export function* handleMarkDiscountCouponsUsed({ payload }) {
  try {
    if (!payload.couponId) {
      throw new Error("Coupon id is required");
    }
    yield call(CouponService.use, { id: payload.couponId });
    yield put(markDiscountCouponsUsedSuccess(payload.id));
    message.success("Coupon marked as used");
    yield put(fetchDiscountCouponsRequest(payload.id));
    yield put(fetchDiscountRequest(payload.id));
  } catch (error) {
    const messageText = error?.message || "Unable to mark coupon as used";
    yield put(markDiscountCouponsUsedFailure(messageText));
    message.error(messageText);
  }
}

export function* handleFetchDiscountApprovals({ payload }) {
  try {
    const response = yield call(DiscountService.approvals, payload);
    const data = getPayloadData(response);
    yield put(fetchDiscountApprovalsSuccess(data));
  } catch (error) {
    const messageText = error?.message || "Unable to load approval queue";
    yield put(fetchDiscountApprovalsFailure(messageText));
    message.error(messageText);
  }
}

export function* handleApproveDiscount({ payload }) {
  try {
    const response = yield call(DiscountService.approve, payload.id, payload.data || {});
    const data = getPayloadData(response);
    yield put(approveDiscountSuccess(data));
    message.success("Discount approved");
    yield put(fetchDiscountApprovalsRequest(payload.params || {}));
    yield put(fetchDiscountHistoryRequest(payload.id));
    yield put(fetchDiscountRequest(payload.id));
  } catch (error) {
    const messageText = error?.message || "Unable to approve discount";
    yield put(approveDiscountFailure(messageText));
    message.error(messageText);
  }
}

export function* handleRejectDiscount({ payload }) {
  try {
    const response = yield call(DiscountService.reject, payload.id, payload.data || {});
    const data = getPayloadData(response);
    yield put(rejectDiscountSuccess(data));
    message.success("Discount rejected");
    yield put(fetchDiscountApprovalsRequest(payload.params || {}));
    yield put(fetchDiscountHistoryRequest(payload.id));
    yield put(fetchDiscountRequest(payload.id));
  } catch (error) {
    const messageText = error?.message || "Unable to reject discount";
    yield put(rejectDiscountFailure(messageText));
    message.error(messageText);
  }
}

export function* handleFetchDiscountHistory({ payload }) {
  try {
    const response = yield call(DiscountService.history, payload);
    const data = getPayloadData(response);
    yield put(fetchDiscountHistorySuccess(payload, data));
  } catch (error) {
    const messageText = error?.message || "Unable to load approval history";
    yield put(fetchDiscountHistoryFailure(messageText));
    message.error(messageText);
  }
}

export function* handleFetchDiscount({ payload }) {
  try {
    const response = yield call(DiscountService.show, payload);
    const data = getPayloadData(response);
    yield put(fetchDiscountSuccess(data));
  } catch (error) {
    yield put(fetchDiscountFailure(error?.message || "Unable to load discount"));
    message.error("Unable to load discount details");
  }
}

export function* handleCreateDiscount({ payload }) {
  try {
    const response = yield call(DiscountService.create, payload);
    const data = getPayloadData(response);
    yield put(createDiscountSuccess(data));
    message.success("Discount created");
    yield put(fetchDiscountRequest(data?.id));
  } catch (error) {
    yield put(createDiscountFailure(error?.message || "Unable to create discount"));
    message.error(error?.message || "Unable to create discount");
  }
}

export function* handleUpdateDiscount({ payload }) {
  try {
    const response = yield call(DiscountService.update, payload.id, payload.data);
    const data = getPayloadData(response);
    yield put(updateDiscountSuccess(data));
    message.success("Discount updated");
  } catch (error) {
    yield put(updateDiscountFailure(error?.message || "Unable to update discount"));
    message.error(error?.message || "Unable to update discount");
  }
}

export function* handleDeleteDiscount({ payload }) {
  try {
    yield call(DiscountService.remove, payload);
    yield put(deleteDiscountSuccess(payload));
    message.success("Discount deleted");
  } catch (error) {
    yield put(deleteDiscountFailure(error?.message || "Unable to delete discount"));
    message.error(error?.message || "Unable to delete discount");
  }
}

export default function* DiscountSaga() {
  yield all([
    takeLatest(FETCH_DISCOUNTS_REQUEST, handleFetchDiscounts),
    takeLatest(FETCH_DISCOUNT_REQUEST, handleFetchDiscount),
    takeLatest(CREATE_DISCOUNT_REQUEST, handleCreateDiscount),
    takeLatest(UPDATE_DISCOUNT_REQUEST, handleUpdateDiscount),
    takeLatest(DELETE_DISCOUNT_REQUEST, handleDeleteDiscount),
    takeLatest(FETCH_DISCOUNT_COUPONS_REQUEST, handleFetchDiscountCoupons),
    takeLatest(GENERATE_DISCOUNT_COUPONS_REQUEST, handleGenerateDiscountCoupons),
    takeLatest(MARK_DISCOUNT_COUPONS_PRINTED_REQUEST, handleMarkDiscountCouponsPrinted),
    takeLatest(MARK_DISCOUNT_COUPONS_USED_REQUEST, handleMarkDiscountCouponsUsed),
    takeLatest(FETCH_DISCOUNT_APPROVALS_REQUEST, handleFetchDiscountApprovals),
    takeLatest(APPROVE_DISCOUNT_REQUEST, handleApproveDiscount),
    takeLatest(REJECT_DISCOUNT_REQUEST, handleRejectDiscount),
    takeLatest(FETCH_DISCOUNT_HISTORY_REQUEST, handleFetchDiscountHistory),
  ]);
}
